import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Stack from "@mui/material/Stack";
import useMediaQuery from "@mui/material/useMediaQuery";
import { muiTheme } from "@polifonia/theme";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { Modal } from "@/components/Modal";
import {
  getApiErrorBodyFromUnknownError,
  handleNonGlobalApiError,
} from "@/features/error/handleError";
import type { InterludeTypes } from "@/interlude";
import { interlude } from "@/interlude";
import { LineCreateForm } from "@/pages/channels/components/LineCreateForm";
import { LineCreateSuccess } from "@/pages/channels/components/LineCreateSuccess";

interface LineCreateModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Connects a LINE official account by its Messaging API channel ID and
 * secret. interlude forwards both to cantata, which validates them and reads
 * the OA's name, so the form only collects the two values.
 */
export const LineCreateModal: React.FC<LineCreateModalProps> =
  function LineCreateModal({ open, onClose }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const isNarrow = useMediaQuery(muiTheme.breakpoints.down("md"));

    const [channelId, setChannelId] = useState("");
    const [channelSecret, setChannelSecret] = useState("");
    /**
     * Server verdicts tied to a field. The values the user typed stay in
     * place so they can be corrected.
     */
    const [channelIdApiError, setChannelIdApiError] = useState("");
    const [channelSecretApiError, setChannelSecretApiError] = useState("");
    /** Set once the OA is connected; switches the modal to the success view. */
    const [createdChannel, setCreatedChannel] = useState<
      InterludeTypes["LineChannelCreateResponse"] | null
    >(null);

    // Start every opening from a blank form; a previous attempt's values,
    // errors and success view belong to a flow the user already left. Reset
    // during render on the closed-to-open transition, not in an effect, so
    // the previous success view never paints for a frame.
    const [wasOpen, setWasOpen] = useState(open);
    if (open !== wasOpen) {
      setWasOpen(open);
      if (open) {
        setChannelId("");
        setChannelSecret("");
        setChannelIdApiError("");
        setChannelSecretApiError("");
        setCreatedChannel(null);
      }
    }

    const createMutation = interlude.line.useCreateChannel(undefined, {
      onSuccess(channel) {
        setCreatedChannel(channel);
      },
      async onError(error) {
        // interlude#1362 answers cantata's 400 with `INVALID_REQUEST`, which
        // does not say whether the ID or the secret is wrong, so the message
        // names both and sits under the last field. The code is also mapped
        // globally, so a generic snackbar appears beside this message.
        if (
          getApiErrorBodyFromUnknownError(error)?.code === "INVALID_REQUEST"
        ) {
          setChannelSecretApiError(
            t(
              "channels.line.create.error.credentialInvalid",
              "The Channel ID or Channel secret is incorrect. Make sure you chose a Messaging API channel.",
            ),
          );
          return;
        }
        await handleNonGlobalApiError(error, {
          // interlude#1362 turns every 409 from cantata into this code, so it
          // covers an OA this org already connected and one connected to
          // another org; the message is worded to fit either.
          CHANNEL_ALREADY_EXISTS() {
            setChannelIdApiError(
              t(
                "channels.line.create.error.alreadyConnected",
                "This LINE Official Account is already connected. To move it to this organization, contact your sales representative.",
              ),
            );
          },
        });
      },
    });

    const submitting = createMutation.isPending;
    const trimmedChannelId = channelId.trim();
    const trimmedChannelSecret = channelSecret.trim();
    const canSubmit =
      trimmedChannelId !== "" && trimmedChannelSecret !== "" && !submitting;

    const handleClose = useHandler(() => {
      if (submitting) return;
      onClose();
    });

    const handleSubmit = useHandler(() => {
      if (!canSubmit) return;
      setChannelIdApiError("");
      setChannelSecretApiError("");
      createMutation.mutate({
        externalChannelId: trimmedChannelId,
        externalChannelSecret: trimmedChannelSecret,
      });
    });

    // Every way out of the success view lands on the new channel's settings,
    // where the list and detail are fetched fresh.
    const handleFinish = useHandler(() => {
      if (!createdChannel) return;
      onClose();
      navigate({
        to: "/channels/$type/$id",
        params: { type: "line", id: createdChannel.uuid },
      });
    });

    const handleChannelIdChange = useHandler((value: string) => {
      setChannelId(value);
      // The credential verdict covers the ID too, so it no longer applies.
      setChannelIdApiError("");
      setChannelSecretApiError("");
    });

    const handleChannelSecretChange = useHandler((value: string) => {
      setChannelSecret(value);
      setChannelSecretApiError("");
    });

    if (createdChannel) {
      return (
        <LineCreateSuccess
          open={open}
          channel={createdChannel}
          onFinish={handleFinish}
        />
      );
    }

    return (
      <Modal
        open={open}
        handleClose={handleClose}
        width={isNarrow ? 520 : 880}
        title={t("channels.line.create.title", "Set up LINE")}
        footer={
          <Stack direction="row" justifyContent="flex-end" gap="12px">
            <Button variant="plain" onClick={handleClose} disabled={submitting}>
              {t("channels.line.create.cancel", "Cancel")}
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={!canSubmit}
              loading={submitting}
            >
              {t("channels.line.create.submit", "Connect")}
            </Button>
          </Stack>
        }
      >
        <LineCreateForm
          narrow={isNarrow}
          channelId={channelId}
          channelSecret={channelSecret}
          channelIdError={channelIdApiError}
          channelSecretError={channelSecretApiError}
          disabled={submitting}
          onChannelIdChange={handleChannelIdChange}
          onChannelSecretChange={handleChannelSecretChange}
        />
      </Modal>
    );
  };
